import { Transition } from '@headlessui/react';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import clsx from 'clsx';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { CarouselImage } from './types';

// Paths of antd's LeftOutlined and RightOutlined, inlined since this package
// can't import @ant-design/icons (see README).
const arrowPaths = {
  prev: 'M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z',
  next: 'M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z',
};

interface PrevNextButtonProps {
  type: 'prev' | 'next';
  onClick: () => void;
}
function PrevNextButton({ onClick, type }: PrevNextButtonProps) {
  return (
    <button
      className={`rounded-full z-50 bg-black border-none bg-opacity-30 absolute flex items-center justify-center top-[50%] translate-y-[-50%] cursor-pointer w-8 h-8 ${
        type === 'prev' ? 'left-3' : 'right-3'
      }`}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      }}
    >
      <svg
        className={'text-white'}
        viewBox="64 64 896 896"
        width="1em"
        height="1em"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d={arrowPaths[type]} />
      </svg>
    </button>
  );
}

/**
 * How the server-rendered cover image loads. 'eager' is the browser default.
 *
 * React 19 emits a <link rel="preload" as="image"> during SSR for every <img>
 * that isn't loading="lazy", so on a 50-card list page every cover was both
 * preloaded and downloaded up front (~1 MB) while only one or two are visible
 * on mobile. Lists should pass 'high' for the first card (the likely LCP image)
 * and 'lazy' for cards that start below the fold.
 */
export type CoverImagePriority = 'high' | 'eager' | 'lazy';

const coverImageAttributes = (
  priority: CoverImagePriority,
): Pick<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'fetchPriority' | 'loading'
> => {
  if (priority === 'high') {
    return { fetchPriority: 'high' };
  }
  if (priority === 'lazy') {
    return { loading: 'lazy' };
  }
  return {};
};

interface Props {
  slides: CarouselImage[];
  options?: EmblaOptionsType;
  view: 'list' | 'card';
  alt: string;
  imagePriority?: CoverImagePriority;
  /**
   * Content of an extra last slide in the card view, on the brand gradient,
   * e.g. a "go to listing" call to action. It gets a dot of its own.
   */
  endSlide?: React.ReactNode;
}

function EmblaCarousel({
  slides: images,
  options,
  view,
  alt,
  imagePriority = 'eager',
  endSlide,
  active,
  inViewRef,
}: Props & {
  active: boolean;
  inViewRef: (node?: Element | null | undefined) => void;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ ...options, duration: 20 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Until the card has been in view only the cover is rendered and Embla isn't
  // attached: useEmblaCarousel builds and measures a carousel as soon as its ref
  // gets a node, even with active: false, which is 50 carousels on a list page.
  const slides = images.slice(0, active ? 3 : 1);
  const viewportRef = active ? emblaRef : undefined;
  const [visible, setVisible] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  if (view === 'card') {
    const radius = 'rounded-2xl';
    const dots = slides
      .map((slide, index) => index)
      .concat(endSlide ? [slides.length] : []);

    return (
      <div
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        ref={inViewRef}
      >
        <div
          className={clsx('absolute bottom-2 z-50 w-full', {
            hidden: slides.length === 1,
          })}
        >
          <div className={'flex space-x-2 w-full items-center justify-center'}>
            {dots.map(index => (
              <div
                key={index}
                className={clsx('rounded-full transition-all', {
                  'bg-opacity-50 bg-white': index !== selectedIndex,
                  'bg-white': index === selectedIndex,
                  'w-2 h-2': slides.length < 5 && index !== selectedIndex,
                  'w-2.5 h-2.5': slides.length < 5 && index === selectedIndex,
                  'w-1 h-1': slides.length >= 5 && index !== selectedIndex,
                  'w-1.5 h-1.5': slides.length >= 5 && index === selectedIndex,
                })}
              ></div>
            ))}
          </div>
        </div>
        <div className="overflow-hidden" ref={viewportRef}>
          <div
            className={`flex max-h-full w-full ${radius} transform-gpu gap-2`}
          >
            {slides?.map((image, i) => {
              const imageAlt = i === 0 ? alt : `${alt} – bild ${i + 1}`;

              return (
                <div
                  className={`flex-[0_0_100%] min-w-0 relative ${radius} transform-gpu border border-solid border-gray-100`}
                  key={i}
                >
                  <AspectRatio ratio={3 / 2}>
                    <div
                      className={clsx(
                        'w-full h-full absolute bg-gradient-to-br from-zinc-200 to-white z-[-5]',
                        radius,
                      )}
                    />
                    <img
                      className={`h-full object-cover w-full ${radius}`}
                      src={image.url}
                      alt={imageAlt}
                      {...(i === 0 && coverImageAttributes(imagePriority))}
                    />
                  </AspectRatio>
                </div>
              );
            })}
            {active && endSlide && (
              <div
                className={`flex-[0_0_100%] min-w-0 relative ${radius} transform-gpu items-center flex flex-col justify-center bg-coral prose-sm`}
                style={{
                  background:
                    'linear-gradient(225deg, rgba(255, 115, 105, 1), rgba(136, 90, 228, 1))',
                }}
              >
                {endSlide}
              </div>
            )}
          </div>

          {active && (
            <Transition
              show={!!slides?.length && slides?.length > 1 && visible}
              enter="transition-opacity duration-75"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <PrevNextButton type={'prev'} onClick={scrollPrev} />
              <PrevNextButton type={'next'} onClick={scrollNext} />
            </Transition>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      className={
        'rounded-tl-xl rounded-bl-xl w-[375px] desktop:w-[300px] h-full relative'
      }
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      ref={inViewRef}
    >
      <div
        className={clsx('absolute bottom-2 z-10 w-full', {
          hidden: slides.length === 1,
        })}
      >
        <div className={'flex space-x-2 w-full items-center justify-center'}>
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={clsx('rounded-full transition-all', {
                'bg-opacity-50 bg-white': index !== selectedIndex,
                'bg-white': index === selectedIndex,
                'w-2 h-2': slides.length < 5 && index !== selectedIndex,
                'w-2.5 h-2.5': slides.length < 5 && index === selectedIndex,
                'w-1 h-1': slides.length >= 5 && index !== selectedIndex,
                'w-1.5 h-1.5': slides.length >= 5 && index === selectedIndex,
              })}
            ></div>
          ))}
        </div>
      </div>
      <div
        className="overflow-hidden h-full rounded-tl-xl rounded-bl-xl transform-gpu"
        ref={viewportRef}
      >
        <div className="flex h-full rounded-tl-xl rounded-bl-xl">
          {slides?.map((image, i) => {
            const imageAlt = i === 0 ? alt : `${alt} – bild ${i + 1}`;

            return (
              <div
                className="flex-[0_0_100%] h-full min-w-0 relative transform-gpu"
                key={i}
              >
                <img
                  className={`block h-full w-full object-cover rounded-tl-xl rounded-bl-xl`}
                  src={image.url}
                  alt={imageAlt}
                  {...(i === 0 && coverImageAttributes(imagePriority))}
                />
              </div>
            );
          })}
        </div>

        {active && (
          <Transition
            show={!!slides?.length && slides?.length > 1 && visible}
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <PrevNextButton type={'prev'} onClick={scrollPrev} />
            <PrevNextButton type={'next'} onClick={scrollNext} />
          </Transition>
        )}
      </div>
    </div>
  );
}

/**
 * Cover image carousel for listing cards: dots and arrows, no thumbnails.
 *
 * Always renders the carousel markup, with Embla attached only once the card has
 * been in view. The server-rendered cover <img> is then the same element React
 * hydrates and Embla takes over, instead of a static <img> that gets swapped for
 * a new one after hydration. That swap let the new <img> become the LCP element
 * whenever hydration beat the cover's first paint, pushing LCP past all the JS.
 */
function CoverCarousel(props: Props) {
  const { inView, ref } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  return <EmblaCarousel {...props} active={inView} inViewRef={ref} />;
}

export default CoverCarousel;
